import { forwardRef, type ReactNode, type SelectHTMLAttributes } from 'react';
import { CaretDown, WarningCircle } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Field, fieldControlClass, type FieldSize } from '../Field/Field';
import { Icon } from '../Icon';

export type SelectOption = { value: string; label: string; disabled?: boolean };

export type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> & {
  label: ReactNode;
  labelHidden?: boolean;
  help?: ReactNode;
  error?: ReactNode;
  options: SelectOption[];
  /** Fill the container's width (default) or size to the longest option. */
  fullWidth?: boolean;
  /** `md` (default) is 48px; `sm` is 40px for desktop toolbars and filter rows. */
  size?: FieldSize;
  fieldClassName?: string;
};

/**
 * A native select styled like Input: keeps the platform picker (and its accessibility) on every
 * device. More than five mutually exclusive options belong here rather than in a SegmentedControl.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, labelHidden, help, error, options, fullWidth = true, id, className, fieldClassName, size = 'md', ...rest },
  ref,
) {
  return (
    <Field label={label} labelHidden={labelHidden} help={help} error={error} id={id} className={fieldClassName}>
      {({ id: fieldId, describedBy, invalid }) => (
        <div className={cx('relative', fullWidth ? 'w-full' : 'w-fit')}>
          <select
            {...rest}
            ref={ref}
            id={fieldId}
            aria-invalid={invalid || undefined}
            aria-describedby={cx(describedBy, rest['aria-describedby']) || undefined}
            className={cx(
              fieldControlClass(invalid, size),
              'cursor-pointer appearance-none font-semibold',
              invalid ? 'pr-16' : 'pr-10',
              className,
            )}
          >
            {options.map((o) => (
              <option key={o.value} value={o.value} disabled={o.disabled}>
                {o.label}
              </option>
            ))}
          </select>
          {invalid ? (
            <span className="pointer-events-none absolute inset-y-0 right-10 flex items-center text-jc-ink">
              <Icon icon={WarningCircle} size="md" weight="fill" />
            </span>
          ) : null}
          <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-jc-ink-secondary">
            <Icon icon={CaretDown} size="sm" />
          </span>
        </div>
      )}
    </Field>
  );
});
